type DiffType = "added" | "removed" | "modified" | "unchanged";

interface DiffDetail {
  type: DiffType;
  description?: string;
  changes?: string[];
}

interface EndpointDiff {
  path: string;
  method: string;
  status: DiffType;
  parameterDiffs?: DiffDetail[];
  requestBodyDiff?: DiffDetail;
  responseBodyDiff?: DiffDetail;
}

interface OpenApiDiffResult {
  endpointDiffs: EndpointDiff[];
}

/**
 * Recursively compares two objects and returns list of textual diffs.
 * */
function deepCompare(obj1: any, obj2: any, path = ""): string[] {
  const diffs: string[] = [];
  const keys = new Set([
    ...Object.keys(obj1 ?? {}),
    ...Object.keys(obj2 ?? {}),
  ]);

  keys.forEach((key) => {
    const val1 = obj1?.[key];
    const val2 = obj2?.[key];
    const currentPath = path ? `${path}.${key}` : key;

    if (val1 === undefined) {
      diffs.push(`Missing in first spec: ${currentPath}`);
    } else if (val2 === undefined) {
      diffs.push(`Missing in second spec: ${currentPath}`);
    } else if (
      typeof val1 === "object" &&
      val1 !== null &&
      typeof val2 === "object" &&
      val2 !== null
    ) {
      diffs.push(...deepCompare(val1, val2, currentPath));
    } else if (val1 !== val2) {
      diffs.push(
        `Value differs at ${currentPath}: '${JSON.stringify(
          val1
        )}' vs '${JSON.stringify(val2)}'`
      );
    }
  });

  return diffs;
}

/**
 * Main diff function: compares two OpenAPI specs and returns differences.
 */
export function diffOpenApiSpecs(spec1: any, spec2: any): OpenApiDiffResult {
  const endpointDiffs: EndpointDiff[] = [];

  const paths1 = spec1.paths || {};
  const paths2 = spec2.paths || {};

  const allPaths = new Set([...Object.keys(paths1), ...Object.keys(paths2)]);

  allPaths.forEach((path) => {
    const methods1 = paths1[path] || {};
    const methods2 = paths2[path] || {};
    const allMethods = new Set([
      ...Object.keys(methods1),
      ...Object.keys(methods2),
    ]);

    allMethods.forEach((method) => {
      const op1 = methods1[method];
      const op2 = methods2[method];

      if (!op1) {
        endpointDiffs.push({
          path,
          method,
          status: "added",
        });
      } else if (!op2) {
        endpointDiffs.push({
          path,
          method,
          status: "removed",
        });
      } else {
        // Compare parameters by "name-in" identity
        const params1 = op1.parameters || [];
        const params2 = op2.parameters || [];
        const paramDiffs: DiffDetail[] = [];

        const paramKeys1 = params1.map((p: any) => `${p.name}-${p.in}`);
        const paramKeys2 = params2.map((p: any) => `${p.name}-${p.in}`);

        paramKeys1.forEach((p: any) => {
          if (!paramKeys2.includes(p)) {
            paramDiffs.push({
              type: "removed",
              description: `Parameter '${p}' removed`,
            });
          }
        });

        paramKeys2.forEach((p: any) => {
          if (!paramKeys1.includes(p)) {
            paramDiffs.push({
              type: "added",
              description: `Parameter '${p}' added`,
            });
          }
        });

        // Compare requestBody
        let requestBodyDiff: DiffDetail | undefined;
        if (
          JSON.stringify(op1.requestBody) !== JSON.stringify(op2.requestBody)
        ) {
          requestBodyDiff = {
            type: "modified",
            description: "Request body changed",
            changes: deepCompare(
              op1.requestBody,
              op2.requestBody,
              "requestBody"
            ),
          };
        }

        // Compare responses - focus on '200' or 'default'
        const resp1 = op1.responses?.["200"] ?? op1.responses?.default;
        const resp2 = op2.responses?.["200"] ?? op2.responses?.default;

        let responseBodyDiff: DiffDetail | undefined;
        if (JSON.stringify(resp1) !== JSON.stringify(resp2)) {
          responseBodyDiff = {
            type: "modified",
            description: "Response body changed",
            changes: deepCompare(resp1, resp2, "response"),
          };
        }

        const hasDiff =
          paramDiffs.length > 0 || requestBodyDiff || responseBodyDiff;

        endpointDiffs.push({
          path,
          method,
          status: hasDiff ? "modified" : "unchanged",
          parameterDiffs: paramDiffs.length ? paramDiffs : undefined,
          requestBodyDiff,
          responseBodyDiff,
        });
      }
    });
  });

  return { endpointDiffs };
}
